import React from 'react';
import { 
  Phone, 
  Brain, 
  FileText, 
  Pill, 
  Bell, 
  DollarSign,
  ArrowRight
} from 'lucide-react';

const ProductModulesSection = ({ isDark }) => {
  const modules = [
    {
      id: 'voice',
      title: 'Voice Agent',
      description: 'Handles inbound calls, books appointments, and captures symptoms before the visit.',
      icon: Phone,
      color: 'sky',
      span: 'col-span-1 md:col-span-8',
      features: ['24/7 call handling', 'Multi-language', 'Smart triage']
    },
    {
      id: 'copilot',
      title: 'Clinical Copilot',
      description: 'Real-time differential diagnosis with WHO/NICE guidelines.',
      icon: Brain,
      color: 'violet',
      span: 'col-span-1 md:col-span-4',
      features: ['Evidence-based', 'Local protocols']
    },
    {
      id: 'documentation',
      title: 'Documentation Engine',
      description: 'SOAP/EMR-ready notes with auto-generated ICD-10 codes.',
      icon: FileText,
      color: 'emerald',
      span: 'col-span-1 md:col-span-4',
      features: ['SOAP format', 'ICD-10 coding']
    },
    {
      id: 'execution',
      title: 'Care Execution Layer',
      description: 'Prescriptions, lab tests, and imaging with direct EMR integration.',
      icon: Pill,
      color: 'amber',
      span: 'col-span-1 md:col-span-8',
      features: ['EMR sync', 'E-prescribing', 'Lab ordering']
    },
    {
      id: 'followup',
      title: 'Follow-up Automation',
      description: 'Automated reminders and outcome tracking.',
      icon: Bell,
      color: 'rose',
      span: 'col-span-1 md:col-span-6',
      features: ['SMS automation', 'Outcome tracking']
    },
    {
      id: 'revenue',
      title: 'Revenue Engine',
      description: 'Claim-ready summaries and ROI dashboards.',
      icon: DollarSign,
      color: 'teal',
      span: 'col-span-1 md:col-span-6',
      features: ['Billing optimization', 'Revenue dashboards']
    }
  ];

  const getColorClasses = (color, isDark) => {
    const colors = {
      sky: { 
        bg: isDark ? 'bg-sky-900/40' : 'bg-sky-50', 
        icon: 'text-sky-500', 
        pill: isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-50 text-sky-700' 
      },
      violet: { 
        bg: isDark ? 'bg-violet-900/40' : 'bg-violet-50', 
        icon: 'text-violet-500', 
        pill: isDark ? 'bg-violet-900/40 text-violet-400' : 'bg-violet-50 text-violet-700' 
      },
      emerald: { 
        bg: isDark ? 'bg-emerald-900/40' : 'bg-emerald-50', 
        icon: 'text-emerald-500', 
        pill: isDark ? 'bg-emerald-900/40 text-emerald-400' : 'bg-emerald-50 text-emerald-700' 
      },
      amber: { 
        bg: isDark ? 'bg-amber-900/40' : 'bg-amber-50', 
        icon: 'text-amber-500', 
        pill: isDark ? 'bg-amber-900/40 text-amber-400' : 'bg-amber-50 text-amber-700' 
      },
      rose: { 
        bg: isDark ? 'bg-rose-900/40' : 'bg-rose-50', 
        icon: 'text-rose-500', 
        pill: isDark ? 'bg-rose-900/40 text-rose-400' : 'bg-rose-50 text-rose-700' 
      },
      teal: { 
        bg: isDark ? 'bg-teal-900/40' : 'bg-teal-50', 
        icon: 'text-teal-500', 
        pill: isDark ? 'bg-teal-900/40 text-teal-400' : 'bg-teal-50 text-teal-700' 
      }
    };
    return colors[color];
  };

  return (
    <section 
      id="product" 
      className={`section-padding ${isDark ? 'bg-slate-800' : 'bg-stone-50'}`}
      data-testid="product-modules-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header - Minimal */}
        <div className="text-center mb-14">
          <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-6 ${
            isDark ? 'bg-sky-900/40 text-sky-400' : 'bg-sky-100 text-sky-700'
          }`}>
            Product Modules
          </span>
          <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-semibold mb-5 tracking-tight ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}>
            Modular AI. Unified Workflow.
          </h2>
          <p className={`text-lg max-w-xl mx-auto ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Six integrated modules working together as one clinical operating system.
          </p>
        </div>

        {/* Bento Grid - Cleaner */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 lg:gap-5">
          {modules.map((module) => {
            const colors = getColorClasses(module.color, isDark);
            const Icon = module.icon;

            return (
              <div 
                key={module.id}
                className={`p-6 rounded-2xl transition-all hover:-translate-y-0.5 ${module.span} ${
                  isDark 
                    ? 'bg-slate-900/50 hover:bg-slate-900' 
                    : 'bg-white shadow-sm hover:shadow-md'
                }`}
                data-testid={`module-${module.id}`}
              >
                <div className="flex flex-col h-full">
                  {/* Icon */}
                  <div className={`w-10 h-10 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-5 h-5 ${colors.icon}`} />
                  </div>

                  {/* Content */}
                  <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {module.title}
                  </h3>
                  <p className={`text-sm mb-4 flex-grow ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {module.description}
                  </p>

                  {/* Feature pills - minimal */}
                  <div className="flex flex-wrap gap-1.5">
                    {module.features.map((feature, index) => (
                      <span 
                        key={index}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium ${colors.pill}`}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA - subtle */}
        <div className="mt-10 text-center">
          <button 
            className={`inline-flex items-center gap-2 text-sm font-medium hover:gap-3 transition-all ${
              isDark ? 'text-sky-400' : 'text-sky-600'
            }`}
            data-testid="explore-all-modules-btn"
          >
            Explore all modules
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductModulesSection;
